import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { PageHeader, DateRangePicker } from '../components/common';
import { Pagination, StatusBadge } from '../components/shopperPayout';
import { ROUTES } from '../constants';
import shopperPayoutService, {
  saveTextAsFile,
} from '../services/shopperPayoutService';

const PAGE_SIZE = 20;
const MIN_KEYWORD_LENGTH = 3;

/**
 * Build the request payload for the history list out of URL search params.
 * @param {Object} params
 * @returns {Object}
 */
const buildRequestParams = params => {
  const requestParams = {
    page: parseInt(params.page, 10) || 1,
    size: parseInt(params.size, 10) || PAGE_SIZE,
  };

  if (params.keyword && params.keyword.length >= MIN_KEYWORD_LENGTH) {
    requestParams.keyword = params.keyword;
  }

  if (params.dateFrom && params.dateTo) {
    requestParams.dateFrom = params.dateFrom;
    requestParams.dateTo = params.dateTo;
  }

  return requestParams;
};

/**
 * Serialize the current filter state back into URL search params so the filter
 * survives navigating into an import and back.
 * @param {Object} state
 * @returns {Object}
 */
const serializeFilters = state => {
  const params = {};
  if (state.page && state.page !== 1) params.page = state.page;
  if (state.keyword && state.keyword.length >= MIN_KEYWORD_LENGTH) {
    params.keyword = state.keyword;
  }
  if (state.dateFrom && state.dateTo) {
    params.dateFrom = state.dateFrom;
    params.dateTo = state.dateTo;
  }
  return params;
};

const ShopperPayoutPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialLoadRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: PAGE_SIZE,
    total: 0,
  });

  const fetchImports = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const data = await shopperPayoutService.getImportList(params);

      setImports(data.data || []);
      setPagination({
        page: data.page || 1,
        pageCount: data.pageCount || 1,
        size: data.size || PAGE_SIZE,
        total: data.total || 0,
      });
    } catch (err) {
      setError(
        shopperPayoutService.getErrorMessage(
          err,
          'Failed to load the import history. Please try again.'
        )
      );
      setImports([]);
      setPagination(prev => ({ ...prev, pageCount: 1, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  // Restore the filter from the URL on first render.
  useEffect(() => {
    if (hasInitialLoadRef.current) return;
    hasInitialLoadRef.current = true;

    const params = Object.fromEntries(searchParams.entries());
    setSearchTerm(params.keyword || '');
    setStartDate(params.dateFrom || '');
    setEndDate(params.dateTo || '');
    setPagination(prev => ({
      ...prev,
      page: parseInt(params.page, 10) || 1,
    }));

    fetchImports(buildRequestParams(params));
  }, [fetchImports, searchParams]);

  const applyFilters = useCallback(() => {
    const nextState = {
      page: 1,
      keyword: searchTerm,
      dateFrom: startDate,
      dateTo: endDate,
    };

    setSearchParams(serializeFilters(nextState), { replace: true });
    fetchImports(buildRequestParams(nextState));
  }, [searchTerm, startDate, endDate, fetchImports, setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSearchParams({}, { replace: true });
    fetchImports({ page: 1, size: PAGE_SIZE });
  }, [fetchImports, setSearchParams]);

  const handlePageChange = useCallback(
    page => {
      const nextState = {
        page,
        keyword: searchTerm,
        dateFrom: startDate,
        dateTo: endDate,
      };

      setSearchParams(serializeFilters(nextState), { replace: true });
      fetchImports(buildRequestParams(nextState));
    },
    [searchTerm, startDate, endDate, fetchImports, setSearchParams]
  );

  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    setError(null);

    try {
      const { content, fileName } = await shopperPayoutService.getTemplate();
      saveTextAsFile(content, fileName);
    } catch (err) {
      setError(
        shopperPayoutService.getErrorMessage(
          err,
          'Failed to download the CSV template.'
        )
      );
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const showingFrom =
    pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.size + 1;
  const showingTo = Math.min(
    pagination.page * pagination.size,
    pagination.total
  );

  const keywordTooShort =
    searchTerm.length > 0 && searchTerm.length < MIN_KEYWORD_LENGTH;

  return (
    <div>
      <PageHeader
        title='Shoppers Payout'
        subtitle='Manage your shopper payout.'
        breadcrumbs={[
          { name: 'Dashboard', href: ROUTES.DASHBOARD },
          { name: 'Shopper Payout' },
        ]}
      />

      {/* Filters */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div>
            <label
              htmlFor='searchFileName'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search File Name
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='searchFileName'
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder='Search by file name (min 3 characters)...'
                className={`block w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  keywordTooShort
                    ? 'border-yellow-300 bg-yellow-50'
                    : 'border-gray-300'
                }`}
              />
              {keywordTooShort && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {MIN_KEYWORD_LENGTH - searchTerm.length} more
                  </div>
                </div>
              )}
            </div>
          </div>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onDateRangeChange={({ startDate: start, endDate: end }) => {
              setStartDate(start);
              setEndDate(end);
            }}
            placeholder='Search by date...'
            label={{ start: 'Uploaded Date (From', end: 'To)' }}
          />

          <div className='flex flex-col justify-end'>
            <div className='flex gap-2'>
              <button
                type='button'
                onClick={clearFilters}
                className='flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
              >
                Clear Filters
              </button>
              <button
                type='button'
                onClick={applyFilters}
                disabled={loading}
                className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? 'Searching...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2'>
          <svg
            className='w-5 h-5 text-red-400 flex-shrink-0 mt-0.5'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
            />
          </svg>
          <p className='text-sm text-red-700 whitespace-pre-line flex-1'>
            {error}
          </p>
          <button
            type='button'
            onClick={() => setError(null)}
            aria-label='Dismiss error'
            className='text-red-400 hover:text-red-600'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      )}

      {/* History Import */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
              History Import
              {loading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
              )}
            </h3>
            <p className='text-sm text-gray-500 mt-1'>
              Showing {shopperPayoutService.formatCount(showingFrom)} to{' '}
              {shopperPayoutService.formatCount(showingTo)} of{' '}
              {shopperPayoutService.formatCount(pagination.total)} history file
              imported.
            </p>
          </div>

          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              className='border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {downloadingTemplate ? 'Downloading...' : 'CSV Template'}
            </button>
            <button
              type='button'
              onClick={() => navigate(ROUTES.SHOPPER_PAYOUT_IMPORT)}
              className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            >
              Bulk Import
            </button>
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Import ID
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  File Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Uploaded By
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Uploaded Date
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Valid
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Invalid
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {imports.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan='8'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>No imports found</p>
                      <p className='text-sm'>
                        Try adjusting your search criteria or start a new bulk
                        import.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                imports.map(item => (
                  <tr key={item.id} className='hover:bg-gray-50'>
                    <td className='px-4 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {item.batchCode || '-'}
                    </td>
                    <td className='px-3 py-4 text-sm text-gray-900 break-all max-w-xs'>
                      {item.fileName || '-'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {item.uploadedByName || '-'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {item.uploadedAt
                        ? moment(item.uploadedAt).format('DD MMMM YYYY HH:mm')
                        : '-'}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right'>
                      {shopperPayoutService.formatCount(item.validRows)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right'>
                      {shopperPayoutService.formatCount(item.invalidRows)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <StatusBadge value={item.status} kind='import' />
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center'>
                      <button
                        type='button'
                        onClick={() =>
                          navigate(
                            ROUTES.generatePath(ROUTES.SHOPPER_PAYOUT_DETAIL, {
                              id: item.id,
                            })
                          )
                        }
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          pageCount={pagination.pageCount}
          onPageChange={handlePageChange}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default ShopperPayoutPage;
